/*
 * Assisted + Influenced Revenue: the rules about the two columns, kept apart from the table so
 * they can be reasoned about (and tested) on their own.
 *
 * An assist is a conversion the ad was part of the journey for but that the attribution model
 * credited to another ad. Both columns are optional, live on the Ad tab only, and only exist
 * under First Click or Last Click - the backend decides that and says so in the payload.
 */

export const ASSISTED_METRIC = 'Assisted';
export const INFLUENCED_REVENUE_METRIC = 'Influenced Revenue';

// Canonical order: Conversions | Assisted | Influenced Revenue.
export const ASSIST_METRICS = [ASSISTED_METRIC, INFLUENCED_REVENUE_METRIC];

export const isAssistMetric = (metric: string): boolean => ASSIST_METRICS.includes(metric);

/* Sort tokens, merged into the goal-metric map the table already sorts by, so the two columns
   go through the existing "goal:<goal key>:<metric>" mechanism rather than a new one. */
export const ASSIST_SORT_FIELD: Record<string, string> = {
  [ASSISTED_METRIC]: 'assisted',
  [INFLUENCED_REVENUE_METRIC]: 'influenced_revenue',
};

// Wording is pinned server-side too; this is the fallback when the payload has not arrived.
export const ASSISTS_UNAVAILABLE_MESSAGE =
  'Assisted Conversion is available under First Click or Last Click attribution. Switch attribution to see assists.';

/* Which of the two the column chooser may offer for a goal. Influenced Revenue needs a goal
   that carries money — a $0 goal gets Assisted alone. */
export const assistMetricsOfferedForGoal = (goalHasRevenue: boolean): string[] =>
  goalHasRevenue ? [...ASSIST_METRICS] : [ASSISTED_METRIC];

/*
 * The columns the table actually draws for one goal. The chooser's selection is shared across
 * Campaign / Ad Set / Ad, so the two assist columns are filtered back out anywhere but the Ad
 * tab, and Influenced Revenue is dropped for a goal with no monetary value.
 */
export const visibleGoalMetrics = (
  metrics: string[],
  opts: { isAdLevel: boolean; goalHasRevenue: boolean }
): string[] =>
  metrics.filter((m) => {
    if (!isAssistMetric(m)) { return true; }
    if (!opts.isAdLevel) { return false; }
    if (m === INFLUENCED_REVENUE_METRIC) { return opts.goalHasRevenue; }
    return true;
  });

/*
 * What "All" writes for a goal.
 *
 * The per-goal selection is one list shared by the Campaign, Ad Set and Ad tabs, but each
 * drawer offers only the columns its own tab can show - the assist columns are offered from Ad
 * settings alone. Assigning the offered list wholesale would therefore let "All" in the Campaign
 * drawer silently delete the Ad tab's assist columns. Anything the current drawer cannot offer
 * is carried through untouched: "All" adds columns, it never removes them.
 */
export const selectAllMetrics = (offered: string[], existing: string[]): string[] => {
  const offeredSet = new Set(offered);
  const kept = existing.filter((m) => !offeredSet.has(m));
  return [...offered, ...kept];
};

/*
 * What "None" clears for a goal. Same reasoning in reverse: a drawer clears the columns it
 * offers, and leaves alone the ones it never showed the user.
 */
export const clearMetrics = (offered: string[], existing: string[]): string[] => {
  const offeredSet = new Set(offered);
  return existing.filter((m) => !offeredSet.has(m));
};

/*
 * Which goal definitions a tab renders with.
 *
 * has_revenue is measured per level, so the Ad tab has to read the Ad payload's own flag. Falling
 * back to whichever payload happened to load first made the Influenced Revenue column appear or
 * vanish depending on whether the user had visited the Campaign tab, so a tab prefers its own
 * response and only borrows another as a placeholder before its own arrives.
 */
export const pickGoalDefs = <T extends { key: string }>(own: T[] | undefined | null, fallback: T[]): T[] =>
  Array.isArray(own) && own.length > 0 ? own : fallback;

/*
 * Which goal NAMES may offer an Influenced Revenue column.
 *
 * Goals are identified by key - webhook:<id>, goal:<id>, postback:<id>, button:<n> - but the
 * per-goal column selection this flag gates is addressed by name, and one name can cover
 * several keys: a combined report unions the goals of every selected campaign, so two
 * campaigns that each have a "Purchase" webhook contribute two keys under one name.
 *
 * The flags are therefore OR-ed, never assigned. Assigning let whichever goal came last decide
 * the name, so a campaign whose Purchase carried no money in the window switched Influenced
 * Revenue off for every other campaign's Purchase as well - the column simply vanished as soon
 * as a second campaign was selected. A single-campaign report is unaffected: one goal per name
 * makes OR the identity.
 */
export const goalHasRevenueByName = <T extends { name: string; has_revenue?: number }>(
  goals: T[]
): Record<string, boolean> => {
  const out: Record<string, boolean> = {};
  for (const g of goals) { out[g.name] = !!out[g.name] || !!g.has_revenue; }
  return out;
};

/*
 * Where the assist columns belong in the canonical goal order: right after Conversions. The
 * column chooser and the tables both build their list through this, so the chooser lists the
 * columns in the order the table draws them.
 */
export const withAssistMetrics = (metrics: string[], assists: string[] = ASSIST_METRICS): string[] => {
  if (assists.length === 0) { return [...metrics]; }
  const rest = metrics.filter((m) => !isAssistMetric(m));
  const at = rest.indexOf('Conversions');
  const insertAt = at >= 0 ? at + 1 : 0;
  return [...rest.slice(0, insertAt), ...assists, ...rest.slice(insertAt)];
};

/*
 * Default placement: switching an assist column on drops it in right after Conversions rather
 * than at the far end of the goal group. Only runs when one was just switched on, so a column
 * the user has since dragged somewhere else stays where they put it.
 */
export const positionAssistMetrics = (ordered: string[], newlyAdded: string[] = []): string[] => {
  if (!ASSIST_METRICS.some((m) => newlyAdded.includes(m))) { return ordered; }
  const present = ASSIST_METRICS.filter((m) => ordered.includes(m));
  if (present.length === 0) { return ordered; }
  const rest = ordered.filter((m) => !isAssistMetric(m));
  const afterConversions = rest.indexOf('Conversions');
  const insertAt = afterConversions >= 0 ? afterConversions + 1 : 0;
  return [...rest.slice(0, insertAt), ...present, ...rest.slice(insertAt)];
};

/*
 * Whether clicking an Assisted cell can open the journey drawer. Nothing to drill into when the
 * model produced no assists (null) or the ad assisted none, so the cell stays plain text.
 */
export const canOpenAssistJourney = (
  assisted: number | null | undefined,
  assistsSupported: boolean
): boolean => assistsSupported && typeof assisted === 'number' && assisted > 0;

/*
 * On-demand loading.
 *
 * Building a journey is the most expensive query the Ad tab issues, so the report no longer
 * computes assists for every ad on load. A cell therefore has three states, and telling them
 * apart matters: "-" means the model cannot measure assists at all, a number means it was
 * fetched, and anything else means it can be fetched but has not been.
 */
export type AssistFetchState = 'unavailable' | 'idle' | 'loading' | 'loaded';

export const assistCellKey = (adId: string, goalKey: string): string => `${adId}|${goalKey}`;

export const assistFetchState = (opts: {
  assistsSupported: boolean;
  assistsLoaded: boolean;      // figures already came with the report payload
  fetched: boolean;            // figures arrived from an on-demand call
  loading: boolean;
}): AssistFetchState => {
  if (!opts.assistsSupported) { return 'unavailable'; }
  if (opts.assistsLoaded || opts.fetched) { return 'loaded'; }
  return opts.loading ? 'loading' : 'idle';
};

/* One request carries Assisted and Influenced Revenue together, so opening either column
   resolves both and a second click never issues a duplicate query. */
export type AssistValues = { assisted: number; influenced_revenue: string | null };

/*
 * Explaining WHY an ad counts as Assisted.
 *
 * The journey drawer lists every touchpoint in the session, which on its own does not show what
 * the number means. Assisted says "this ad was in the journey but another ad won", so the two
 * roles have to be legible at a glance: the ad being counted, and the ad that took the credit.
 *
 * Nothing here re-derives attribution. The credited touchpoint is read from the `attribution`
 * field the journey API already returns ('First Click', 'Last Click' or 'First Click,Last Click',
 * written from campaign_first_click / campaign_last_click), and the assisting touchpoint is
 * simply the one carrying the ad whose figure was clicked.
 */
export type TouchpointRole = 'assisting' | 'credited' | 'both' | 'other';

/* The report may carry either the token or the label; the journey shows labels. */
export const attributionLabelOf = (raw: string): string => {
  const v = (raw || '').toLowerCase();
  if (v === 'first_click' || v === 'first click') { return 'First Click'; }
  if (v === 'last_click'  || v === 'last click')  { return 'Last Click'; }
  return raw || '';
};

export const classifyTouchpoint = (opts: {
  adValues: string[];       // every ad id shown on this touchpoint
  attribution: string;      // what the journey API reported for this row
  viewedAdId: string;       // the ad whose Assisted figure was clicked (already decoded)
  attributionModel: string; // the report's model, token or label
}): TouchpointRole => {
  const label = attributionLabelOf(opts.attributionModel);
  const isViewedAd = !!opts.viewedAdId && opts.adValues.some((v) => String(v) === opts.viewedAdId);
  /* 'First Click,Last Click' means one click was both ends of the journey, so the row is the
     winner under either model - split before comparing rather than matching the whole string. */
  const isCredited = !!label && (opts.attribution || '')
    .split(',')
    .map((s) => s.trim())
    .includes(label);
  if (isViewedAd && isCredited) { return 'both'; }
  if (isCredited) { return 'credited'; }
  if (isViewedAd) { return 'assisting'; }
  return 'other';
};

/*
 * Cell text. null is what the backend sends when the attribution model cannot produce assists,
 * and it must read as "not measured" (—), never as a zero.
 */
export const assistCellValue = (
  metric: string,
  goalMetric: { assisted?: number | null; influenced_revenue?: string | null } | undefined
): string => {
  if (!goalMetric) { return '-'; }
  if (metric === ASSISTED_METRIC) {
    const v = goalMetric.assisted;
    return v === null || v === undefined ? '—' : v.toLocaleString();
  }
  const rev = goalMetric.influenced_revenue;
  return rev === null || rev === undefined || rev === '' ? '—' : rev;
};
